import { colorForTestResult } from './colors'
import { getAttachmentUrl } from './reports'
import { ATTACHMENT_TYPE } from '../statics/constants'
import appRoutes from '../statics/appRoutes'

const REDIRECT_MARKER = 'redirect-'

const getRedirectPath = () => {
  const url = window.location.href
  const qMarkPosn = url.indexOf('?')
  if (qMarkPosn < 0) {
    return ''
  }
  const qParams = url.indexOf('&') > -1 ? url.substring(qMarkPosn + 1).split('&') : url.split('?').slice(1)
  for (const qParam of qParams) {
    if (qParam.startsWith(REDIRECT_MARKER) && qParam.length > REDIRECT_MARKER.length) {
      return qParam.substring(REDIRECT_MARKER.length)
    }
  }
  return ''
}

const getValidRegexForAppRoute = (route) => {
  return new RegExp('^' + route.replace(/:[^\s/]+/g, '[^/]+') + '$')
}

const validAppRoutesRegexes = Object.values(appRoutes).map(getValidRegexForAppRoute)

const isValidRoute = (path) => {
  return validAppRoutesRegexes.some((regex) => regex.test(path))
}

const encodeUrl = (url) => {
  return encodeURIComponent(`${REDIRECT_MARKER}${url}`)
}

const decodeUrl = (url) => {
  return decodeURIComponent(url)
}

const getTagColor = (testResult) => {
  testResult = testResult.toLowerCase()
  switch (testResult) {
    case 'passed':
      return 'green'
    case 'failed':
      return 'red'
    case 'didnotfinish':
      return 'gold'
    case 'cancelled':
      return 'pink'
    default:
      return 'default'
  }
}

const getTestResultColor = (result) => {
  switch (result) {
    case 'Passed':
      return colorForTestResult.passed
    case 'Failed':
      return colorForTestResult.failed
    case 'DidNotFinish':
      return colorForTestResult.didnotfinish
    default:
      return colorForTestResult.cancelled
  }
}

const getImageAsBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl)

    if (!response.ok) {
      throw new Error(`Failed to fetch image. Status code: ${response.status}`)
    }

    const blob = await response.blob()
    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    throw new Error('Failed to fetch image.')
  }
}

const getTableAttachment = async (runId, tableId) => {
  const tableUrl = await getAttachmentUrl(runId, ATTACHMENT_TYPE.TABLE, tableId)
  try {
    const response = await fetch(tableUrl)
    if (!response.ok) {
      throw new Error(`Failed to fetch table. Status code: ${response.status}`)
    }
    return await response.text()
  } catch (error) {
    throw new Error('Failed to fetch table.')
  }
}

const transformParam = async (param, runId, fetchBase64 = false) => {
  let arg = {}
  if (param.Value && typeof param.Value === 'object') {
    const type = param.Value.ArgType ? param.Value.ArgType : param.Value.$type
    switch (type) {
      case 'PlainText':
      case 'ArgPlainText':
        arg = {
          value: param.Value.Text,
          type
        }
        break
      case 'ArgIdentifier':
      case 'Identifier':
      case 'SimpleIdentifier':
      case 'ArgSimpleIdentifier':
        arg = {
          value: param.Value.Method + '=' + param.Value.Specification,
          type
        }
        break
      case 'ImageIdentifier':
      case 'ArgImageIdentifier':
        const imageUrl = await getAttachmentUrl(runId, ATTACHMENT_TYPE.IMAGE, param.Value.Id)
        if (fetchBase64) {
          var imageBase64 = await getImageAsBase64(imageUrl)
        }
        arg = {
          id: param.Value.Id,
          value: fetchBase64 ? imageBase64 : imageUrl,
          type
        }
        break
      case 'Table':
        arg = {
          id: param.Value.Id,
          type
        }
        break
      case 'Secret':
      case 'ArgObscureSecret':
        // Reports generated with V1 don't have the Text field
        arg = {
          value: param.Value?.Text || '******',
          type: 'Secret'
        }
        break
      default:
        arg = {
          value: param?.Value?.Text || 'No value found',
          type
        }
        break
    }
  } else if (typeof param.Value === 'string') {
    arg = {
      value: param.Value,
      type: typeof param.Value
    }
  }
  return { Id: param.Key, Name: param.Name, Argument: arg }
}

const scrollToSection = (ref) => {
  const headerHeight = 64
  if (ref.current) {
    const yOffset = ref.current.getBoundingClientRect().top - headerHeight
    window.scrollTo({
      top: yOffset,
      behavior: 'smooth'
    })
  }
}

const allowedSequences = ['TestSequence', 'CompositeStep', 'Loop', 'Iteration']

const findTableInRun = (run, tableId) => {
  const getTableInputsInNodes = (root) => {
    return root.flatMap(step => {
      const tableInputs = (step.Parameters || []).filter(value => value.Value.ArgType === 'Table')
      const childTableInputs = step.Nodes ? getTableInputsInNodes(step.Nodes) : []
      return [...tableInputs, ...childTableInputs]
    })
  }
  const findTableInTestParams = (params, tableId) =>
    params.filter(param => param.Value.ArgType === 'Table')
      .find(table => table.Value.Id === tableId)

  return findTableInTestParams(run.Parameters, tableId) ??
    findTableInTestParams(run.TestEnvironment.Variables, tableId) ??
      findTableInTestParams(getTableInputsInNodes(run.Root.Nodes), tableId)
}

export {
  getRedirectPath,
  encodeUrl,
  decodeUrl,
  getTagColor,
  getTestResultColor,
  transformParam,
  scrollToSection,
  allowedSequences,
  isValidRoute,
  getTableAttachment,
  findTableInRun
}
